import { GAEvent, GAEventNames } from 'game/analytics/events';
import { fadeUI } from 'game/core/Playable2DRootContainer';
import { EventName } from 'game/events/eventsName';
import { GameEvent } from 'game/events/gameEvent';
import { QuestManager } from 'game/quests/QuestManager';
import { ActionType } from 'game/quests/types';
import { ADS_RECEIVE_WAND_COUNTER, DEFAULT_HINT_COUNTER, DEFAULT_UNDO_COUNTER, Store } from 'game/store/gameStore';
let adsTimer: NodeJS.Timeout | null = null;

export type AdsPlacement = 'undo' | 'hint' | 'wand' | 'mission' | 'new-missions-request';
export class AdsManager {
    place: AdsPlacement | null = null;
    adGranted = false;
    bannerTrigger = 'New Game' as 'Restart' | 'New Game' | 'Win Popup';
    adsReady = false;
    showAdsCb: (() => void) | null = null;
    constructor() {
        GameEvent.add(EventName.SHOW_ADS, this.showAds.bind(this));

        this.onAdsLoaded = this.onAdsLoaded.bind(this);

        addEventListener('gptScriptLoaded', this.onAdsLoaded);

        addEventListener('dismissRewardedAd', this.dismissRewardedAd.bind(this));
        addEventListener('rewardedSlotGranted', this.rewardedSlotGranted.bind(this));

        addEventListener('adBreakAfter', this.adBreakAfter.bind(this));
        addEventListener('adBreakBefore', this.adBreakBefore.bind(this));
        addEventListener('adBreakDone', this.adBreakDone.bind(this));
        addEventListener(
            'newGame',
            () => {
                this.showBannerAds('New Game');
            },
            false
        );
        addEventListener(
            'newWinGame',
            () => {
                this.showBannerAds('Win Popup');
            },
            false
        );
        addEventListener(
            'restartGame',
            () => {
                this.showBannerAds('Restart');
            },
            false
        );
        addEventListener('noSlotsRewardedAd', this.noSlotsRewardedAd.bind(this), false);
        GameEvent.add(EventName.CLOSE_MODAL, this.onCloseModal.bind(this));
        GameEvent.add(EventName.OPEN_MODAL, this.onOpenModal.bind(this));


        
        this.onAdsLoaded();
    }
    onAdsLoaded() {
        removeEventListener('gptScriptLoaded', this.onAdsLoaded);
        this.adsReady = true;
        if (this.showAdsCb) {
            this.showAdsCb();
            this.showAdsCb = null;
        }
    }
    adBreakAfter() {
        GAEvent(GAEventNames.ADS_BREAK_DONE, {
            trigger: this.bannerTrigger,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });

        if (Store.gameOver) return;
        Store.resumeGame();
    }
    adBreakBefore() {
        GAEvent(GAEventNames.ADS_BREAK_START, {
            trigger: this.bannerTrigger,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });

        if (Store.gameOver) return;
        Store.resumeGame();
    }

    adBreakDone() {
        if (Store.gameOver) return;
        Store.resumeGame();
    }

    showBannerAds(place: 'Restart' | 'New Game' | 'Win Popup' = 'New Game') {
        this.bannerTrigger = place;
        if (adsTimer) clearTimeout(adsTimer);
        // GAEvent(GAEventNames.ADS_BREAK_POPUP, { // we show instantly for now
        //     trigger: place,
        //     type: Store.getGameTypeText(),
        //     difficulty: Store.getDifficultyText(),
        // });

        Store.pauseGame();
        switch (place) {
            case 'Restart':
                dispatchEvent(new CustomEvent('showAdBanner', { detail: { type: 'start', name: 'restart' } }));
                break;
            case 'New Game':
                dispatchEvent(new CustomEvent('showAdBanner', { detail: { type: 'start', name: 'new-game' } }));
                break;
            case 'Win Popup':
                dispatchEvent(new CustomEvent('showAdBanner', { detail: { type: 'next', name: 'win-game' } }));
                break;
        }
    }
    showAds({ place }: { place: AdsPlacement }) {
        Store.startGame();

        this.adGranted = false;
        this.place = place;
        GAEvent(GAEventNames.ADS_REWARDER_CLICK, {
            trigger: place,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });

        Store.pauseGame();
        GameEvent.emit(EventName.SHOW_LOADER);
        fadeUI(0.5);
        if (this.adsReady) {
            dispatchEvent(new CustomEvent('showAd', { detail: { place } }));
        }
        else {
            this.showAdsCb = () => {
                dispatchEvent(new CustomEvent('showAd', { detail: { place } }));
            };
        }
    }
    dismissRewardedAd() {
        GameEvent.emit(EventName.HIDE_LOADER, 0);
        fadeUI(1, 0);
        GameEvent.emit(EventName.ADS_CLOSED);
        Store.resumeGame();
        if (!this.adGranted)
            GAEvent(GAEventNames.ADS_REWARDER_SKIP, {
                trigger: this.place || 'unknown',
                type: Store.getGameTypeText(),
                difficulty: Store.getDifficultyText(),
            });
    }

    rewardedSlotGranted(e: { detail: { place: AdsPlacement } }) {
        GAEvent(GAEventNames.ADS_REWARDER_RECEIVE, {
            trigger: e.detail.place,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });
        this.adGranted = true;
        switch (e.detail.place) {
            case 'undo':
                Store.setUndoCounter(DEFAULT_UNDO_COUNTER);
                break;
            case 'hint':
                Store.setHintCounter(DEFAULT_HINT_COUNTER);
                break;
            case 'wand':
                Store.setWandCounter(ADS_RECEIVE_WAND_COUNTER);
                break;
            case 'mission':
                GameEvent.emit(EventName.MISSION_ACTION, { type: ActionType.AD, payload: { adWatched: 1 }, currentProgress: 1 });
                break;
            case 'new-missions-request':
                QuestManager.setupNewMission();
                break;
        }
        GameEvent.emit(EventName.ADS_GRANTED, e.detail.place);
    }

    noSlotsRewardedAd(e: { detail: { place: AdsPlacement; reason: string; event?: unknown } }) {
        GameEvent.emit(EventName.HIDE_LOADER, 0);
        fadeUI(1);
        GAEvent(GAEventNames.ADS_REWARDER_ERROR, {
            trigger: e.detail.place,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
            slot: JSON.stringify(e.detail.event.slot),
            isBackfill: JSON.stringify(e.detail.event.isBackfill),
        });
        this.adGranted = true;
        switch (e.detail.place) {
            case 'undo':
                Store.setUndoCounter(DEFAULT_UNDO_COUNTER);
                break;
            case 'hint':
                Store.setHintCounter(DEFAULT_HINT_COUNTER);
                break;
            case 'wand':
                Store.setWandCounter(ADS_RECEIVE_WAND_COUNTER);
                break;
            case 'mission':
                GameEvent.emit(EventName.MISSION_ACTION, { type: ActionType.AD, payload: { adWatched: 1 }, currentProgress: 1 });
                break;
            case 'new-missions-request':
                QuestManager.setupNewMission();
                break;

        }

        GameEvent.emit(EventName.ADS_GRANTED, e.detail.event.slot);
        this.dismissRewardedAd();
    }
    adBlockerHandler() {
        GameEvent.emit(EventName.HIDE_LOADER, 0);
        fadeUI(1, 0);
        GameEvent.emit(EventName.ADS_CLOSED);
        Store.resumeGame();
    }
    onCloseModal({ id, confirm }: { id: string; confirm: boolean }) {
        switch (id) {
            case 'adblocker':
                GAEvent(GAEventNames.AD_BLOCKER_POPUP_CLOSE, {
                    trigger: this.place || 'unknown',
                    type: Store.getGameTypeText(),
                    difficulty: Store.getDifficultyText(),
                });
                this.adBlockerHandler();
                break;
        }
    }
    onOpenModal({ id }: { id: string }) {
        switch (id) {
            case 'adblocker':
                GAEvent(GAEventNames.AD_BLOCKER_POPUP_SHOW, {
                    trigger: this.place || 'unknown',
                    type: Store.getGameTypeText(),
                    difficulty: Store.getDifficultyText(),
                });
                break;
        }
    }
}
